import React, { memo } from 'react';
import { CheckCircle2, FileText, Upload, ExternalLink, Link2, Plus, Settings2, NotebookPen } from 'lucide-react';
import { Surface } from '../layout/Surface';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { linkLabel, normalizeUrl, resolveLinkIcon } from '../links/linkMeta';
import { cn } from '../../lib/utils';

/**
 * ProjectBar — the console's instrument strip.
 *
 * This is where the project's name and icon live now. They used to be a page
 * header floating above the content, which stops working the moment the content
 * becomes a mosaic of independently resizable panes: a header above a mosaic
 * belongs to nothing. As a `bar` surface it is a wrapper like every other one,
 * peer to the panes rather than hovering over them — the same move
 * TaskControlBar made over the board.
 *
 * It also absorbs the old floating action bar, which was `fixed bottom-6` and
 * sat on top of whatever was underneath it.
 *
 * The service links were three buttons hotlinking logo PNGs from ai-bot.cn,
 * vectorseek.com and logos-world.net — someone else's uptime rendering inside
 * our UI, and 60px of width each for something clicked once a week. They are a
 * dropdown now, and they finally include `github`, which onboarding has always
 * written and Home has never shown.
 *
 * ── Why this bar measures itself and not the window ──────────────────────────
 * Every breakpoint here used to be a viewport one. That was true for exactly as
 * long as the bar spanned the route: the moment the scratchpad opens, the console
 * moves the bar inside the `command` column, whose width is a seam the user can
 * drag from 62% down to 34%. On a 1440px screen that is a ~450px box still
 * matching `lg:`, so the right-hand cluster — which is `flex-shrink-0` and
 * ~570px wide with its labels showing — simply ran through the project name and
 * out the far edge, where Surface's overflow-hidden ate it.
 *
 * So the bar is a container, and every tier below is measured against its own
 * box. The ladder sheds in order of what you can live without: labels, then the
 * completion bar and the secondary links, then the readouts, then the extra
 * quick actions — leaving identity, capture, and a menu holding everything else.
 *
 * ── The menu is not a container ──────────────────────────────────────────────
 * Radix portals DropdownMenuContent to document.body, so `@[…]/bar:` variants
 * mean nothing inside it. That is why the menu lists *every* link rather than
 * only the ones that didn't fit: a link whose icon is hidden at this width has
 * to still be reachable, and the menu is the only place that can promise it.
 */

// How many links get to be a one-click icon. The rest are menu-only, and the
// settings editor draws this same boundary in its list so the two agree.
const PINNED_LINK_COUNT = 3;

const openLink = (url) => window.open(normalizeUrl(url), '_blank', 'noopener,noreferrer');

/**
 * A pinned link: icon only, because the bar has no room for four labels.
 *
 * `className` carries the tier at which this one earns its place. Hiding rather
 * than not rendering is deliberate — the count of visible icons is a CSS fact
 * now, and JS has no business trying to predict it.
 */
function LinkButton({ link, index, className }) {
    const Icon = resolveLinkIcon(link);
    const label = linkLabel(link);
    return (
        <button
            type="button"
            onClick={() => openLink(link.url)}
            title={label}
            aria-label={`Open ${label}`}
            // Positional, not by type: every custom link has type "custom", so
            // keying the testid on it would give several elements the same one.
            data-testid={`project-link-${index}`}
            className={cn(
                'h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-muted-foreground transition-colors hover:border-primary/30 hover:bg-primary/10 hover:text-foreground',
                className
            )}
        >
            <Icon className="h-3.5 w-3.5" />
        </button>
    );
}

/** A single readout. The number carries the weight; the label just names it. */
function Stat({ value, label, tone }) {
    return (
        <div className="flex items-baseline gap-1.5">
            <span className={cn('font-mono text-sm font-bold tabular-nums leading-none', tone ?? 'text-foreground')}>
                {value}
            </span>
            <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">
                {label}
            </span>
        </div>
    );
}

function QuickAction({ icon: Icon, label, hint, onClick, testId, className, accent, count }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            title={hint ? `${label} (${hint})` : label}
            className={cn(
                'flex h-8 items-center gap-1.5 rounded-xl border px-2.5 text-xs font-medium transition-colors',
                accent
                    ? 'border-primary/25 bg-primary/10 text-foreground hover:border-primary/40 hover:bg-primary/15'
                    : 'border-white/10 bg-white/[0.03] text-muted-foreground hover:border-white/20 hover:bg-white/[0.06] hover:text-foreground',
                className
            )}
        >
            <Icon className={cn('h-3.5 w-3.5', accent && 'text-primary')} />
            <span className="hidden @[64rem]/bar:inline">{label}</span>
            {/* The count rides on the icon at every width — it's the only hint
                that closed drafts exist when the labels are gone. */}
            {count > 0 ? (
                <span className="rounded-full bg-primary/25 px-1 text-[10px] font-bold tabular-nums text-primary">
                    {count}
                </span>
            ) : null}
            {hint ? (
                <kbd className="hidden font-mono text-[10px] text-muted-foreground/50 @[64rem]/bar:inline">{hint}</kbd>
            ) : null}
        </button>
    );
}

function ProjectBarImpl({
    project,
    stats,
    links = [],
    readOnly,
    draftCount,
    onNewTask,
    onNewDoc,
    onNewDraft,
    onUpload,
    onManageLinks,
}) {
    // A link with no URL can't be opened, so it doesn't get to occupy one of the
    // three slots — the editor drops these on save, but a project written by
    // onboarding may still hold one.
    const usable = links.filter((link) => link.url);

    // Which of these are on screen is a CSS decision, one tier at a time. JS
    // only picks the three that are *eligible* to be icons — it used to also
    // decide how many, from `isMobile`, which was a guess about a box it had no
    // way to measure.
    const pinned = usable.slice(0, PINNED_LINK_COUNT);

    // The menu earns its place if it has links to hold, or if there is somewhere
    // to send you to add some. On a share link it is neither, so it disappears.
    const showMenu = usable.length > 0 || (!readOnly && !!onManageLinks);

    return (
        <Surface as="header" variant="bar" aria-label="Project" className="@container/bar flex-shrink-0">
            {/* The gap tightens with the box. At the narrowest tier every 4px is
                a character of the project name that survives. */}
            <div className="flex items-center gap-2 px-3 py-2 @[36rem]/bar:gap-3">
                {/* Identity. The icon is also what the ambient canvas derives its
                    whole palette from, so showing it here is what explains the
                    colour of everything else on screen. */}
                <div className="flex min-w-0 items-center gap-2.5">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border border-white/10 bg-secondary/50">
                        {project.icon ? (
                            <img src={project.icon} alt="" className="h-full w-full object-cover" />
                        ) : (
                            <span className="font-mono text-sm font-bold text-primary">
                                {project.name.charAt(0).toUpperCase()}
                            </span>
                        )}
                    </div>
                    <h1 className="truncate font-mono text-sm font-bold tracking-tight">{project.name}</h1>
                </div>

                <div className="hidden h-5 w-px flex-shrink-0 bg-white/10 @[36rem]/bar:block" />

                {/* Readouts. Everything here was a whole card on the old page —
                    two numbers apiece, 90px of height each. `flex-shrink-0`
                    because a half-rendered number is worse than no number: below
                    its tier this group is gone, above it there is room. */}
                <div className="hidden flex-shrink-0 items-center gap-4 @[36rem]/bar:flex">
                    <Stat value={stats.open} label="Open" />
                    {stats.dueSoon > 0 ? (
                        <Stat value={stats.dueSoon} label="Due" tone="text-amber-400" />
                    ) : null}
                    <div className="hidden @[46rem]/bar:block">
                        <Stat value={stats.fileCount} label="Files" />
                    </div>
                </div>

                <div className="ml-auto flex flex-shrink-0 items-center gap-2 @[46rem]/bar:gap-3">
                    {/* Completion. A bar rather than the old ring: at bar height a
                        ring big enough to hold legible text is taller than the bar. */}
                    {stats.taskCount > 0 ? (
                        <div className="hidden items-center gap-2 @[46rem]/bar:flex" title={`${stats.completedTasks} of ${stats.taskCount} tasks done`}>
                            <div className="h-1 w-16 overflow-hidden rounded-full bg-white/10">
                                <div
                                    className="h-full rounded-full bg-accent transition-[width] duration-700 ease-out"
                                    style={{ width: `${stats.completionPercent}%` }}
                                />
                            </div>
                            <span className="font-mono text-[11px] font-bold tabular-nums text-accent">
                                {stats.completionPercent}%
                            </span>
                        </div>
                    ) : null}

                    {!readOnly ? (
                        <div className="flex items-center gap-1.5">
                            {/* First and tinted: it's the one-keystroke capture,
                                and the only action here you reach for mid-thought. */}
                            <QuickAction
                                icon={NotebookPen}
                                label="Draft"
                                hint="Q"
                                accent
                                count={draftCount}
                                onClick={onNewDraft}
                                testId="home-new-draft"
                            />
                            <QuickAction icon={CheckCircle2} label="Task" hint="N" onClick={onNewTask} testId="home-new-task" />
                            <QuickAction icon={FileText} label="Doc" hint="D" onClick={onNewDoc} testId="home-new-doc" />
                            {/* Four icon buttons don't fit beside the project name
                                in a 400px box, and this is the one with two
                                other ways in: the `u` shortcut and the New
                                Document dialog's Upload tab. */}
                            <QuickAction
                                icon={Upload}
                                label="Upload"
                                hint="U"
                                onClick={onUpload}
                                testId="home-upload"
                                className="hidden @[28rem]/bar:flex"
                            />
                        </div>
                    ) : null}

                    {/* Links. Up to three as icons, all of them in the menu — the
                        ordering that decides which three is the user's, in
                        settings. The icons are a shortcut to what the menu
                        already holds, which is what lets CSS hide them one by
                        one without ever hiding a link. */}
                    {pinned.length > 0 || showMenu ? (
                        <div className="flex items-center gap-1">
                            {pinned.map((link, i) => (
                                <LinkButton
                                    key={`${link.url}-${i}`}
                                    link={link}
                                    index={i}
                                    // The first icon earns its place early; the
                                    // second and third only once the readouts
                                    // and the completion bar already fit.
                                    className={i === 0 ? 'hidden @[28rem]/bar:flex' : 'hidden @[46rem]/bar:flex'}
                                />
                            ))}

                            {showMenu ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button
                                            type="button"
                                            aria-label={usable.length ? `${usable.length} project links` : 'Add a link'}
                                            title={usable.length ? `${usable.length} links` : 'Add a link'}
                                            data-testid="project-links-menu"
                                            className="flex h-8 min-w-8 flex-shrink-0 items-center justify-center gap-1 rounded-xl border border-white/10 bg-white/[0.03] px-2 text-muted-foreground transition-colors hover:border-white/20 hover:bg-white/[0.06] hover:text-foreground"
                                        >
                                            {/* A total, not a remainder. How many
                                                icons are showing is now a CSS
                                                fact, so "+2 more" would be a
                                                number this button cannot know. */}
                                            {usable.length > 0 ? (
                                                <>
                                                    <Link2 className="h-3.5 w-3.5" />
                                                    <span className="text-[11px] font-bold tabular-nums">{usable.length}</span>
                                                </>
                                            ) : (
                                                <Plus className="h-3.5 w-3.5" />
                                            )}
                                        </button>
                                    </DropdownMenuTrigger>
                                    {/* Portalled by Radix, so the surface's
                                        overflow-hidden can't clip it — and so
                                        `@[…]/bar:` variants mean nothing in
                                        here. Everything, at every width. */}
                                    <DropdownMenuContent
                                        align="end"
                                        sideOffset={8}
                                        className="w-56 rounded-2xl border-white/10 bg-[hsl(240_11%_7%)]/95 backdrop-blur-xl"
                                    >
                                        {usable.map((link, i) => {
                                            const Icon = resolveLinkIcon(link);
                                            return (
                                                <DropdownMenuItem
                                                    key={`${link.url}-${i}`}
                                                    onSelect={() => openLink(link.url)}
                                                    className="cursor-pointer gap-2 rounded-xl text-xs"
                                                >
                                                    <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                                                    <span className="truncate">{linkLabel(link)}</span>
                                                    <ExternalLink className="ml-auto h-3 w-3 flex-shrink-0 text-muted-foreground/50" />
                                                </DropdownMenuItem>
                                            );
                                        })}

                                        {usable.length > 0 && !readOnly && onManageLinks ? (
                                            <DropdownMenuSeparator className="bg-white/10" />
                                        ) : null}

                                        {!readOnly && onManageLinks ? (
                                            <DropdownMenuItem
                                                onSelect={onManageLinks}
                                                className="cursor-pointer gap-2 rounded-xl text-xs"
                                            >
                                                <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
                                                {usable.length > 0 ? 'Manage links' : 'Add a link'}
                                            </DropdownMenuItem>
                                        ) : null}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </div>
        </Surface>
    );
}

export const ProjectBar = memo(ProjectBarImpl);
export default ProjectBar;
